// Story 15.3 — the Screen 24 contract-renewal write (AC2, AC10).
//
// Action-in-path (Rule 06): `POST /admin/orgs/{id}/extend-contract`. The
// endpoint shipped with Story 13.3; this hook is its first product caller.
//
// The body carries `contract_end_date` and NOTHING else. `contract_start_date`
// is echoed by the response but is never written from here — that is 15.6's
// `update-details` slice. A renewal is an UPDATE IN PLACE on the single contract
// pair (FR74): no history row, no rollover, no proration.
//
// INVALIDATE-AND-REFETCH, BOTH KEYS — the detail key the control fired from, and
// the `["admin-orgs"]` PREFIX, because the directory row carries the contract
// end date and every cached filter permutation holds a stale copy of it.

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ADMIN_CONSOLE_QUERY_KEY } from "@/hooks/queries/useAdminConsole";
import { ADMIN_ORG_TOTALS_QUERY_KEY } from "@/hooks/queries/useAdminOrgTotals";
import { ADMIN_ORGS_QUERY_KEY } from "@/hooks/queries/useAdminOrgs";
import { adminOrgDetailQueryKey } from "@/hooks/queries/useAdminOrgDetail";
import { apiFetch } from "@/lib/api-client";

/** Mirrors `extend_contract/schemas.py :: ExtendContractRequest` on the wire. */
export interface ExtendContractRequest {
  contract_end_date: string;
}

/** Mirrors `extend_contract/schemas.py :: ExtendContractData`. */
export interface ExtendContractData {
  organization_id: string;
  contract_start_date: string | null;
  contract_end_date: string;
}

export function extendContractPath(organizationId: string): string {
  return `/admin/orgs/${encodeURIComponent(organizationId)}/extend-contract`;
}

export function useExtendContract(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: ExtendContractRequest) =>
      apiFetch<ExtendContractData>(extendContractPath(organizationId), {
        method: "POST",
        body,
      }),
    // Same pairing as `useRenewContract`: the dialog now gates every dismissal
    // on `isPending` (DW-765 / Franklin#828). The default `"online"` would pause
    // an offline submit forever and lock the operator in with no way out.
    networkMode: "always",
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminOrgDetailQueryKey(organizationId) });
      void queryClient.invalidateQueries({ queryKey: [ADMIN_ORGS_QUERY_KEY] });
      // TWO KEYS. Extending a contract moves the renewal count on both admin
      // screens, and since DW-423 they are two separate reads: "Renewals in 90
      // days" on the console, "Org renewals (90d)" on Screen 23's own totals
      // endpoint. Nothing else here invalidates either.
      void queryClient.invalidateQueries({ queryKey: [ADMIN_CONSOLE_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [ADMIN_ORG_TOTALS_QUERY_KEY] });
    },
  });
}
